import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import { useGlobalConfig } from 'context/GlobalContext';
import { MigrationApis } from 'apis/migrationAPI';
import { MigrationJob } from 'types/apis/migrationInterfaces';
import { MigrationJobList } from './components/MigrationJobList';
import { MigrationUploadForm } from './components/MigrationUploadForm';
import { MigrationJobDetails } from './components/MigrationJobDetails';

export interface MigrationImportModalProps {
  open: boolean;
  onClose: () => void;
  pilotId: string;
  /** Pilot utility locales; drives locale dropdown on New Import. */
  supportedLocales?: string[];
  onMigrationComplete?: () => void;
}

type TabValue = 'history' | 'upload';
type ViewMode = 'list' | 'details' | 'upload';

export const MigrationImportModal: React.FC<MigrationImportModalProps> = ({
  open,
  onClose,
  pilotId,
  supportedLocales,
  onMigrationComplete,
}) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  
  const [activeTab, setActiveTab] = useState<TabValue>('history');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<MigrationJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!pilotId) return;
    setLoading(true);
    const resp = await MigrationApis.listJobs(rc, pilotId);
    if (resp.data) {
      setJobs(resp.data);
      setError(null);
    } else {
      const msg =
        resp.error && typeof resp.error === 'object' && 'message' in resp.error
          ? String((resp.error as { message?: string }).message)
          : 'Failed to fetch migration jobs';
      setError(msg);
    }
    setLoading(false);
  }, [rc, pilotId]);

  useEffect(() => {
    if (open) {
      fetchJobs();
    }
    const currentPollingRef = pollingRef.current;
    return () => {
      if (currentPollingRef) {
        clearInterval(currentPollingRef);
      }
    };
  }, [open, fetchJobs]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    setViewMode(newValue === 'upload' ? 'upload' : 'list');
    setSelectedJob(null);
  };

  const handleJobSelect = (job: MigrationJob) => {
    setSelectedJob(job);
    setViewMode('details');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedJob(null);
    fetchJobs();
  };

  const handleMigrationStarted = (job: MigrationJob) => {
    setSelectedJob(job);
    setViewMode('details');
    setActiveTab('history');
  };

  const handleMigrationComplete = () => {
    fetchJobs();
    onMigrationComplete?.();
  };

  const handleClose = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    setViewMode('list');
    setSelectedJob(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh', maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon color="primary" />
          <Typography variant="h6">Import from Carrom</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {viewMode === 'list' || viewMode === 'upload' ? (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab 
              icon={<HistoryIcon />} 
              iconPosition="start" 
              label="Job History" 
              value="history" 
            />
            <Tab 
              icon={<UploadFileIcon />} 
              iconPosition="start" 
              label="New Import" 
              value="upload" 
            />
          </Tabs>
        </Box>
      ) : null}

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        )}

        {viewMode === 'list' && (
          <MigrationJobList 
            jobs={jobs} 
            loading={loading} 
            onJobSelect={handleJobSelect}
            onRefresh={fetchJobs}
          />
        )}

        {viewMode === 'details' && selectedJob && (
          <MigrationJobDetails
            key={selectedJob.jobId}
            job={selectedJob}
            onBack={handleBackToList}
            onComplete={handleMigrationComplete}
          />
        )}

        {viewMode === 'upload' && (
          <MigrationUploadForm
            pilotId={pilotId}
            supportedLocales={supportedLocales}
            onMigrationStarted={handleMigrationStarted}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

